import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../services/portalApi';
import { CONFIG } from '../constants/config';

const REMEMBERED_EMAIL_KEY = 'patrolit-remembered-email';

export default function LoginPage() {
  // ?demo=1 (the landing page's "Try the demo") prefills the shared demo
  // login. Prefilled rather than auto-submitted so a prospect still sees the
  // real sign-in screen, and can come back later with the visible credentials.
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('demo') === '1';

  const [email, setEmail] = useState(() =>
    isDemo ? CONFIG.DEMO_EMAIL : (localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? ''));
  const [password, setPassword] = useState(() => (isDemo ? CONFIG.DEMO_PASSWORD : ''));
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const setTokens = useAuthStore((s) => s.setTokens);
  const role = useAuthStore((s) => s.role);
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const tokens = await authApi.login(email, password);
      // Don't let a demo sign-in become the remembered address — otherwise
      // the plain Sign in prefills the shared demo account from then on.
      // A real user's own last email still persists.
      if (!isDemo) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
      }
      setTokens(tokens.access_token, tokens.refresh_token);
      const r = role();
      navigate(r ? `/${r}/dashboard` : '/admin/dashboard', { replace: true });
    } catch {
      setError('Invalid email or password.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-sm p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">PatrolIt</h1>
          <p className="text-sm text-gray-500 mt-1">Site Management Portal</p>
        </div>

        {isDemo && (
          <div className="mb-4 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2">
            <p className="text-sm text-brand-800 font-medium">Demo site</p>
            <p className="text-xs text-brand-700 mt-0.5">
              Credentials are filled in — just press Sign in. This is a shared sandbox with
              simulated patrollers and drones; anything you change here is periodically reset.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="admin@yoursite.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                tabIndex={-1}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm transition-colors disabled:opacity-60"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="flex items-center justify-between mt-4 text-xs">
          <Link to="/forgot-password" className="text-brand-600 hover:underline">
            Forgot password?
          </Link>
          <Link to="/register" className="text-brand-600 hover:underline">
            Sign up
          </Link>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          By signing in you agree to our{' '}
          <button
            onClick={() => setShowPrivacy(true)}
            className="text-brand-600 hover:underline"
          >
            Privacy Policy
          </button>
        </p>
      </div>

      {/* Privacy Policy modal */}
      {showPrivacy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Privacy Policy</h2>
            <div className="text-sm text-gray-600 space-y-3 leading-relaxed">
              <p><strong>Data we collect:</strong> Patroller GPS location data during active patrols, alarm events, and portal user account details.</p>
              <p><strong>How we use it:</strong> Location data is used exclusively for patrol tracking and security monitoring within your site. It is not shared with third parties.</p>
              <p><strong>Data retention:</strong> Patrol records and GPS tracks are retained for the duration of your subscription. Account data is removed within 30 days of account closure.</p>
              <p><strong>Access:</strong> Only authorised portal users within your site can access patrol data. Patrollers can only see their own patrol history.</p>
              <p><strong>Security:</strong> All data is transmitted over encrypted connections (TLS). Passwords are stored using industry-standard hashing.</p>
              <p><strong>Contact:</strong> For privacy enquiries contact your site administrator.</p>
              <p className="pt-2">
                <a href="/privacy" target="_blank" rel="noreferrer"
                   className="text-brand-600 hover:underline font-medium">
                  Read the full privacy policy →
                </a>
              </p>
            </div>
            <button
              onClick={() => setShowPrivacy(false)}
              className="mt-6 w-full py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
